import { MAX_DESTINATIONS } from '@repo/api-contracts';
import { useRef, useState } from 'react';

export interface DestinationDraft<T> {
  /** 카드마다 바뀌지 않는 key — React key 이자 제출 오류를 그 카드에 붙이는 이름(카드를 지워도 오류가 옮겨 붙지 않는다). */
  readonly key: string;
  readonly value: T;
}

interface DestinationList<T> {
  readonly items: readonly DestinationDraft<T>[];
  readonly canAdd: boolean;
  readonly canRemove: boolean;
  readonly add: () => void;
  readonly remove: (key: string) => void;
  readonly update: (key: string, patch: Partial<T>) => void;
}

/**
 * 견적 요청 배송지 카드 목록 상태(SUPER-86 spec §6-1) — 1 ~ MAX_DESTINATIONS 장. 첫 카드는 받은 값으로, 추가한 카드는 빈 값으로
 * 시작한다. 회원 폼과 비회원 위젯이 카드 모양만 바꿔 함께 쓴다.
 */
export const useDestinationList = <T>(initial: T, blank: () => T): DestinationList<T> => {
  const next = useRef(1);
  const [items, setItems] = useState<readonly DestinationDraft<T>[]>([
    { key: 'destination-0', value: initial },
  ]);

  const add = (): void => {
    if (items.length >= MAX_DESTINATIONS) return;
    // key·빈 값은 갱신 함수 밖에서 만든다 — 갱신 함수가 순수해야 StrictMode 이중 실행에도 key 가 건너뛰지 않는다.
    const key = `destination-${next.current}`;
    next.current += 1;
    const value = blank();
    setItems((previous) =>
      previous.length >= MAX_DESTINATIONS ? previous : [...previous, { key, value }],
    );
  };

  const remove = (key: string): void => {
    setItems((previous) =>
      previous.length <= 1 ? previous : previous.filter((item) => item.key !== key),
    );
  };

  const update = (key: string, patch: Partial<T>): void => {
    setItems((previous) =>
      previous.map((item) =>
        item.key === key ? { key, value: { ...item.value, ...patch } } : item,
      ),
    );
  };

  return {
    items,
    canAdd: items.length < MAX_DESTINATIONS,
    canRemove: items.length > 1,
    add,
    remove,
    update,
  };
};
